import React from "react";
import { FaArrowLeft, FaTruck, FaClock, FaShieldAlt, FaMapMarkerAlt } from "react-icons/fa";
import { ref, push } from "firebase/database";
import { db } from "../../Pages/OurProduct/firebase";
import { myCustomTheme } from "../../Theme";
import { MASTER_COLOR_PALETTE } from "../../Pages/OurProduct/constants";
import { getDynamicPrices } from "../../Pages/OurProduct/utils/priceUtils";
import { getShippingCharge } from "../../Pages/OurProduct/utils/shippingUtils";
import { sendOrderEmail, generateInvoiceNumber } from "../../Pages/OurProduct/utils/orderUtils";
import { getLockedOrderId } from "../../Pages/OurProduct/orderId";
import "./style/PaymentPage.css";

const getDeliveryInfo = (isCustomized) => {
    const now = new Date();
    const daysToAdd = isCustomized ? 9 : 7;
    const delivery = new Date(now);
    delivery.setDate(delivery.getDate() + daysToAdd);

    const deliveryDate = delivery.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
    });

    const deliveryTime = "6:00 PM";

    return { deliveryDate, deliveryTime, daysToAdd };
};

// ── Price helper (same logic as CartPage) ────────────────────
const getItemDisplayPrice = (item) => {
    if (item.type === "Customize") {
        return Math.round(item.sellingPrice * 6 * 1.18);
    }
    return Math.round(item.sellingPrice * 1.18);
};

const PaymentPage = ({
    selectedProduct,
    userDetails,
    selectedInch,
    customerDesign,
    setCustomerDesign,
    paymentLoading,
    setPaymentLoading,
    onBack,
    onOrderSuccess,
    showToast
}) => {
    // ── Detect Buy All vs single product ────────────────────────
    const isBuyAll = Boolean(selectedProduct?.cartItems);
    const cartItems = isBuyAll ? selectedProduct.cartItems : null;
    const cartImages = isBuyAll ? (selectedProduct.cartImages || {}) : {};

    const hasAnyCustomized = isBuyAll
        ? cartItems.some(item => item.type === "Customize")
        : selectedProduct?.type === "Customize";

    // ── Single product calculations ──────────────────────────────
    const isCustomized = !isBuyAll && selectedProduct?.type === "Customize";
    const dynamicPrices = !isBuyAll ? getDynamicPrices(selectedProduct, selectedInch, userDetails.quantity) : null;

    const singleProductTotal = !isBuyAll
        ? (isCustomized
            ? dynamicPrices.sp
            : Math.round((selectedProduct?.sellingPrice || 0) * userDetails.quantity * 1.18))
        : 0;

    // ── Buy All: sum all cart items ──────────────────────────────
    const cartTotal = isBuyAll
        ? cartItems.reduce((sum, item) => sum + getItemDisplayPrice(item), 0)
        : 0;

    const productTotal = isBuyAll ? cartTotal : singleProductTotal;

    // ── Shipping charge based on customer address ────────────────
    const shippingInfo = getShippingCharge(userDetails.address || "");
    const shippingCharge = shippingInfo.charge;

    // ── Grand total ──────────────────────────────────────────────
    const finalTotalAmount = productTotal + shippingCharge;

    // ── GST breakdown on product total ───────────────────────────
    const productBase = Math.round(productTotal / 1.18);
    const cgst        = Math.round(productBase * 9 / 100);
    const sgst        = Math.round(productBase * 9 / 100);

    const activeColorObject = !isBuyAll
        ? MASTER_COLOR_PALETTE.find(c => c.id === userDetails.color)
        : null;
    const resolvedColorName = activeColorObject ? activeColorObject.name : "Standard Default";

    const { deliveryDate, deliveryTime, daysToAdd } = getDeliveryInfo(hasAnyCustomized);

    // ── Place Order ──────────────────────────────────────────────
    const handleCashOnDelivery = () => {
        setPaymentLoading(true);
        const finalId = getLockedOrderId();
        const invNum  = generateInvoiceNumber();

        let orderData;

        if (isBuyAll) {
            // Multi-item order
            orderData = {
                orderId:        finalId,
                invoiceNumber:  invNum,
                name:           userDetails.name,
                email:          userDetails.email,
                phone:          userDetails.phone,
                address:        userDetails.address,
                isBuyAll:       true,
                items:          cartItems.map(item => ({
                    id:           item.id,
                    productName:  item.name,
                    productImage: cartImages[item.id] || (item.images && item.images[0]) || "",
                    type:         item.type,
                    price:        getItemDisplayPrice(item),
                    hasCustomDesign: Boolean(cartImages[item.id])
                })),
                productTotal:   productTotal,
                shippingCharge: shippingCharge,
                shippingZone:   shippingInfo.label,
                totalAmount:    finalTotalAmount,
                deliveryDate:   deliveryDate,
                deliveryTime:   deliveryTime,
                date:           new Date().toLocaleDateString("en-IN", {
                    day: "numeric", month: "long", year: "numeric"
                }),
                orderTime:      new Date().toLocaleTimeString("en-IN", {
                    hour: "2-digit", minute: "2-digit"
                }),
                status: "Pending"
            };
        } else {
            // Single product order
            orderData = {
                orderId:        finalId,
                invoiceNumber:  invNum,
                name:           userDetails.name,
                email:          userDetails.email,
                phone:          userDetails.phone,
                address:        userDetails.address,
                quantity:       userDetails.quantity,
                color:          userDetails.color,
                colorName:      resolvedColorName,
                productName:    selectedProduct.name,
                productImage:   (selectedProduct.images && selectedProduct.images[0]) || "",
                selectedInch:   isCustomized ? selectedInch : "N/A",
                productTotal:   productTotal,
                shippingCharge: shippingCharge,
                shippingZone:   shippingInfo.label,
                totalAmount:    finalTotalAmount,
                designImage:    isCustomized ? (customerDesign || "None") : "None",
                isCustomized:   isCustomized,
                deliveryDate:   deliveryDate,
                deliveryTime:   deliveryTime,
                date:           new Date().toLocaleDateString("en-IN", {
                    day: "numeric", month: "long", year: "numeric"
                }),
                orderTime:      new Date().toLocaleTimeString("en-IN", {
                    hour: "2-digit", minute: "2-digit"
                }),
                status: "Pending"
            };
        }

        const ordersRef = ref(db, "orders");
        push(ordersRef, orderData)
            .then(() => {
                if (isBuyAll) {
                    // Pass items array so email shows numbered product list
                    const emailItems = cartItems.map(item => ({
                        name:  item.name,
                        type:  item.type,
                        price: getItemDisplayPrice(item)
                    }));
                    const buyAllProductForEmail = {
                        name:         cartItems.map(i => i.name).join(", "),
                        images:       [cartItems[0]?.images?.[0] || ""],
                        type:         "Non-Customize",
                        sellingPrice: productTotal
                    };
                    sendOrderEmail(userDetails, buyAllProductForEmail, finalId, deliveryDate, invNum, shippingCharge, shippingInfo.label, emailItems)
                        .catch((emailErr) => {
                            console.warn("Buy All email send failed:", emailErr);
                        });
                } else {
                    sendOrderEmail(userDetails, selectedProduct, finalId, deliveryDate, invNum, shippingCharge, shippingInfo.label)
                        .catch((emailErr) => {
                            console.warn("Email send failed:", emailErr);
                        });
                }
                setPaymentLoading(false);
                if (setCustomerDesign) setCustomerDesign(null);
                onOrderSuccess(finalId, invNum, deliveryDate, deliveryTime);
                showToast("Order placed successfully!");
            })
            .catch((err) => {
                console.error("Order save failed:", err);
                setPaymentLoading(false);
                showToast("Order failed. Please try again.");
            });
    };

    return (
        <div
            className="payment-page"
            style={{
                backgroundColor: myCustomTheme.colors.header_footer_bg,
                boxShadow: myCustomTheme.shadows.soft,
                borderRadius: "24px",
                border: `1px solid ${myCustomTheme.colors.high_light}`
            }}
        >
            <button
                className="back-btn"
                onClick={onBack}
                style={{
                    backgroundColor: myCustomTheme.colors.button_bg,
                    color: myCustomTheme.colors.button_text
                }}
            >
                <FaArrowLeft /> Back
            </button>

            <h1 style={{ color: myCustomTheme.colors.heading }}>Order Summary</h1>

            <div
                className="summary-card"
                style={{
                    backgroundColor: myCustomTheme.colors.bg_primary,
                    borderColor: myCustomTheme.colors.high_light,
                    borderRadius: "14px"
                }}
            >
                {/* ── Buy All: show all cart items ── */}
                {isBuyAll ? (
                    <div style={{ marginBottom: "16px" }}>
                        <p style={{ fontWeight: "700", color: myCustomTheme.colors.heading, marginBottom: "10px" }}>
                            {cartItems.length} Item{cartItems.length !== 1 ? "s" : ""} in Order
                        </p>
                        {cartItems.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "10px 0",
                                    borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                                }}
                            >
                                <img
                                    src={cartImages[item.id] || (item.images && item.images[0]) || ""}
                                    alt={item.name}
                                    style={{
                                        width: "56px", height: "56px",
                                        objectFit: "cover", borderRadius: "10px",
                                        border: `1.5px solid ${myCustomTheme.colors.high_light}`,
                                        flexShrink: 0
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    <p style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: myCustomTheme.colors.heading }}>
                                        {item.name}
                                    </p>
                                    <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: myCustomTheme.colors.menu_text }}>
                                        {item.type}{item.type === "Customize" && " · 6\""}
                                        {item.type === "Customize" && cartImages[item.id] && (
                                            <span style={{ color: "#15803d", fontWeight: "700", marginLeft: "6px" }}>✓ Design ready</span>
                                        )}
                                    </p>
                                </div>
                                <span style={{ fontWeight: "800", color: myCustomTheme.colors.heading, fontSize: "14px", whiteSpace: "nowrap" }}>
                                    ₹{getItemDisplayPrice(item)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* ── Single product image + info ── */
                    <>
                        {selectedProduct?.images && selectedProduct.images[0] && (
                            <div style={{ marginBottom: "16px" }}>
                                <img
                                    src={selectedProduct.images[0]}
                                    alt={selectedProduct.name}
                                    style={{
                                        width: "80px", height: "80px",
                                        objectFit: "cover", borderRadius: "12px",
                                        border: `2px solid ${myCustomTheme.colors.high_light}`
                                    }}
                                />
                            </div>
                        )}
                        <p><strong>Product:</strong> {selectedProduct?.name}</p>
                        {selectedProduct?.availableColors?.length > 0 && (
                            <p><strong>Color:</strong> {resolvedColorName}</p>
                        )}
                        {isCustomized && (
                            <p><strong>Size:</strong> {selectedInch} inches</p>
                        )}
                        <p><strong>Quantity:</strong> {userDetails.quantity} unit{userDetails.quantity > 1 ? "s" : ""}</p>
                    </>
                )}

                {/* ── Price Breakdown ── */}
                <div style={{
                    marginTop: "14px",
                    borderRadius: "12px",
                    border: `1.5px solid ${myCustomTheme.colors.high_light}`,
                    overflow: "hidden"
                }}>
                    {/* Product subtotal row */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "11px 16px",
                        borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                    }}>
                        <span style={{ fontSize: "13.5px", color: myCustomTheme.colors.menu_text }}>
                            {isBuyAll ? `${cartItems.length} Items Total` : "Product Total"}{" "}
                            <span style={{ fontSize: "11px", color: "#15803d", fontWeight: "600" }}>(GST incl.)</span>
                        </span>
                        <span style={{ fontSize: "14px", fontWeight: "700", color: myCustomTheme.colors.heading }}>
                            ₹{productTotal}
                        </span>
                    </div>

                    {/* GST breakdown (informational) */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 16px",
                        background: "#fffbeb",
                        borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                    }}>
                        <span style={{ fontSize: "12px", color: "#92400e" }}>
                            CGST 9% + SGST 9% (included)
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: "#92400e" }}>
                            ₹{cgst + sgst}
                        </span>
                    </div>

                    {/* Shipping row */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "11px 16px",
                        borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                    }}>
                        <span style={{ fontSize: "13.5px", color: myCustomTheme.colors.menu_text, display: "flex", alignItems: "center", gap: "6px" }}>
                            <FaTruck style={{ color: "#15803d", fontSize: "13px" }} />
                            Shipping Charges
                            <span style={{ fontSize: "11px", color: "#64748b" }}>({shippingInfo.label})</span>
                        </span>
                        <span style={{
                            fontSize: "14px", fontWeight: "700",
                            color: shippingInfo.isFree ? "#15803d" : myCustomTheme.colors.heading
                        }}>
                            {shippingInfo.isFree ? "FREE" : `₹${shippingCharge}`}
                        </span>
                    </div>

                    {/* Grand total row */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 16px",
                        background: "#0f172a"
                    }}>
                        <span style={{ fontSize: "14px", fontWeight: "700", color: "#f1f5f9" }}>
                            Grand Total (Amount Payable)
                        </span>
                        <span style={{ fontSize: "17px", fontWeight: "800", color: "#ffffff" }}>
                            ₹{finalTotalAmount}
                        </span>
                    </div>
                </div>

                {/* Shipping address pincode info */}
                {shippingInfo.pincode && (
                    <div style={{
                        marginTop: "10px", padding: "8px 14px",
                        background: "#f8fafc", border: `1px solid ${myCustomTheme.colors.high_light}`,
                        borderRadius: "8px", fontSize: "12px", color: "#64748b",
                        display: "flex", alignItems: "center", gap: "6px"
                    }}>
                        <FaMapMarkerAlt style={{ color: "#3b82f6", fontSize: "12px" }} />
                        Shipping to pincode <strong>{shippingInfo.pincode}</strong> — {shippingInfo.label}
                    </div>
                )}

                {/* GST included note */}
                <div style={{
                    marginTop: "10px",
                    padding: "10px 14px",
                    background: "#f0fdf4",
                    border: "1.5px solid #86efac",
                    borderRadius: "10px",
                    fontSize: "12.5px",
                    fontWeight: "600",
                    color: "#15803d",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                }}>
                    <FaShieldAlt style={{ fontSize: "13px", color: "#16a34a", flexShrink: 0 }} />
                    18% GST already included in product price. Shipping charge added separately.
                </div>

                {/* Delivery info */}
                <div style={{
                    marginTop: "16px",
                    padding: "14px 16px",
                    background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
                    borderRadius: "10px",
                    border: "1.5px solid #86efac"
                }}>
                    <p style={{ margin: 0, fontWeight: "700", color: "#14532d", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaTruck /> Estimated Delivery
                    </p>
                    <p style={{ margin: "4px 0 0 0", fontWeight: "800", color: "#15803d", fontSize: "15px" }}>
                        {deliveryDate}
                    </p>
                    <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#166534", display: "flex", alignItems: "center", gap: "5px" }}>
                        <FaClock style={{ fontSize: "11px" }} />
                        by {deliveryTime} &nbsp;·&nbsp;
                        {hasAnyCustomized
                            ? `Includes customized item(s): ${daysToAdd} days`
                            : `Standard delivery in ${daysToAdd} days`}
                    </p>
                </div>
            </div>

            <div className="payment-methods">
                <div
                    className="payment-card active-payment"
                    style={{
                        backgroundColor: myCustomTheme.colors.button_bg,
                        color: myCustomTheme.colors.button_text,
                        borderRadius: "10px"
                    }}
                >
                    <FaTruck style={{ marginRight: "8px", display: "inline-block" }} />
                    Cash On Delivery (COD) Selected
                </div>
            </div>

            <button
                className="continue-btn"
                disabled={paymentLoading}
                onClick={handleCashOnDelivery}
                style={{
                    backgroundColor: myCustomTheme.colors.button_bg,
                    color: myCustomTheme.colors.button_text,
                    borderRadius: "10px"
                }}
            >
                {paymentLoading ? "Processing..." : `Confirm COD Order — ₹${finalTotalAmount}`}
            </button>
        </div>
    );
};

export default PaymentPage;